import { useState } from 'react'

import dayjs from 'dayjs'
import { AiOutlinePlus } from 'react-icons/ai'
import {
  CreateSystemMessage,
  CreateSystemMessageVariables,
  DeleteSystemMessage,
  DeleteSystemMessageVariables,
  GetSystemMessages,
  GetSystemMessagesVariables,
  UpdateSystemMessage,
  UpdateSystemMessageInput,
  UpdateSystemMessageVariables,
} from 'types/graphql'

import { useMutation, useQuery } from '@cedarjs/web'
import { toast } from '@cedarjs/web/toast'

import { DeleteIcon } from 'src/assets/DeleteIcon'
import HeadingLevel from 'src/components/Custom/Heading/Heading'
import Loader from 'src/components/Custom/Loader/Loader'
import MessagingTable from 'src/components/Custom/MessagingTable/MessagingTable'
import ConfigureSystemMessageModal from 'src/components/Custom/Modal/ConfigureSystemMessageModal'
import CustomModal from 'src/components/Custom/Modal/CustomModal'

import { TableHeadingType } from '../../common/types'
import Button from '../../components/Custom/Button/Button'

import {
  CREATE_SYSTEM_MESSAGE,
  DELETE_SYSTEM_MESSAGE,
  GET_SYSTEM_MESSAGES,
  UPDATE_SYSTEM_MESSAGE,
} from './queries'

export type SystemMessageType = GetSystemMessages['systemMessages'][number]

const SystemMessagesSection = () => {
  const [showMessageModal, setShowMessageModal] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [editingMessage, setEditingMessage] = useState<SystemMessageType>(null)
  const [deletingMessage, setDeletingMessage] =
    useState<SystemMessageType>(null)

  const {
    data: { systemMessages = [] } = {},
    loading: loadingSystemMessages,
    refetch: refetchSystemMessages,
  } = useQuery<GetSystemMessages, GetSystemMessagesVariables>(
    GET_SYSTEM_MESSAGES
  )

  const handleMessageModalClose = () => {
    setShowMessageModal(false)
    setEditingMessage(null)
  }

  const [createSystemMessage] = useMutation<
    CreateSystemMessage,
    CreateSystemMessageVariables
  >(CREATE_SYSTEM_MESSAGE, {
    onCompleted: () => {
      toast.success('Message created')
      handleMessageModalClose()
      refetchSystemMessages()
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const [updateSystemMessage] = useMutation<
    UpdateSystemMessage,
    UpdateSystemMessageVariables
  >(UPDATE_SYSTEM_MESSAGE, {
    onCompleted: () => {
      toast.success('Message updated')
      handleMessageModalClose()
      refetchSystemMessages()
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const [deleteSystemMessage] = useMutation<
    DeleteSystemMessage,
    DeleteSystemMessageVariables
  >(DELETE_SYSTEM_MESSAGE, {
    onCompleted: () => {
      toast.success('Message deleted')
      setDeletingMessage(null)
      setShowConfirmModal(false)
      refetchSystemMessages()
    },
  })

  const messagesTableHeading: Array<TableHeadingType> = [
    {
      id: 'title-heading',
      field: 'title',
      label: 'MESSAGE TITLE',
    },
    {
      id: 'status-heading',
      field: 'status',
      label: 'STATUS',
    },
    {
      id: 'start-heading',
      field: 'start',
      label: 'START',
    },
    {
      id: 'end-heading',
      field: 'end',
      label: 'END',
    },
    {
      id: 'actions-heading',
      field: 'actions',
      label: 'ACTIONS',
    },
  ]

  const processMessages = (messages: SystemMessageType[]) => {
    return messages.map((message) => {
      const { startAt, endAt, id } = message
      const start = dayjs(startAt)
      const end = dayjs(endAt)
      const today = dayjs()

      let statusTitle = ''
      let statusType = ''
      let live = false

      // Message is expired
      if (end.isBefore(today)) {
        statusTitle = 'Inactive'
        statusType = 'pink'
      }
      // Message is not active yet
      else if (start.isAfter(today)) {
        statusTitle = 'Scheduled'
        statusType = 'warning'
      }
      // Otherwise, Message is live
      else {
        statusTitle = 'Active'
        statusType = 'success'
        live = true
      }

      // Create the edit button JSX
      const actions = (
        <nav className="rw-table-actions">
          {/* Activate/deactivate button */}
          <button
            className="rw-button rw-button-small rw-button-blue normal-case"
            onClick={() => {
              const dateNowString = () => {
                const dateObject = new Date()
                return dateObject.toISOString()
              }
              const input: UpdateSystemMessageInput = {}
              if (live) {
                // If message is live, deactivate by setting endAt to now.
                input.endAt = dateNowString()
              } else {
                // If message is not live, activate by setting startAt to now and endAt to never.
                input.startAt = dateNowString()
                input.endAt = null
              }
              updateSystemMessage({ variables: { id, input } })
            }}
          >
            {live ? 'Deactivate' : 'Send now'}
          </button>
          <button
            className="rw-button rw-button-small rw-button-blue normal-case"
            onClick={() => {
              setEditingMessage(message)
              setShowMessageModal(true)
            }}
          >
            Edit
          </button>
          <button
            className="rw-button rw-button-small rw-button-red"
            title={`Delete system message${message.title && ` "${message.title}"`}`}
            onClick={() => {
              setDeletingMessage(message)
              setShowConfirmModal(true)
            }}
          >
            <DeleteIcon classStyle="fill-red" />
          </button>
        </nav>
      )

      return {
        ...message,
        start: dayjs(startAt).format('MM/DD/YY'),
        end: endAt ? dayjs(endAt).format('MM/DD/YY') : 'Never',
        statusTitle: statusTitle,
        statusType: statusType,
        actions,
      }
    })
  }

  const processedMessages = processMessages(systemMessages)

  const handleMessageSave = (input, id: string) => {
    if (id) {
      updateSystemMessage({ variables: { id, input } })
    } else {
      createSystemMessage({ variables: { input } })
    }
  }

  return (
    <>
      <div className="mt-12 flex items-center justify-between gap-x-2">
        <HeadingLevel level={2} className="align-middle">
          Messages
        </HeadingLevel>
        <Button
          title="Create message"
          icon={<AiOutlinePlus size="15px" />}
          onClick={() => setShowMessageModal(true)}
          backgroundColor={'bg-indigo-600 hover:bg-indigo-800'}
        />
      </div>

      {loadingSystemMessages ? (
        <div className="flex flex-1 items-center justify-center">
          <Loader />
        </div>
      ) : processedMessages?.length ? (
        <MessagingTable
          tableHeading={messagesTableHeading}
          contentList={processedMessages}
        />
      ) : null}

      {showMessageModal && (
        <ConfigureSystemMessageModal
          header={editingMessage?.id ? 'Edit message' : 'Create new message'}
          messageData={editingMessage}
          showModal={showMessageModal}
          handleModalClose={handleMessageModalClose}
          onSave={handleMessageSave}
        />
      )}

      {showConfirmModal && (
        <CustomModal
          setShowModal={setShowConfirmModal}
          showModal={showConfirmModal}
          modalWidth="max-w-md"
        >
          <HeadingLevel level={3}>Delete message?</HeadingLevel>
          <p className="pt-2 text-sm text-gray-500">
            {`This action cannot be undone. This will permanently delete the
              message${deletingMessage.title && ` "${deletingMessage.title}"`}.`}
          </p>
          <div className="float-right mt-5 flex gap-2">
            <Button
              title="Cancel"
              customButtonClass={'rounded'}
              onClick={() => setShowConfirmModal(false)}
            />
            <Button
              title="Delete"
              customButtonClass={
                'bg-red-500 rounded font-bold text-white hover:text-white hover:bg-red-700'
              }
              onClick={() => {
                const deletingMessageId = deletingMessage?.id
                if (deletingMessageId) {
                  deleteSystemMessage({
                    variables: { id: deletingMessageId },
                  })
                }
              }}
            />
          </div>
        </CustomModal>
      )}
    </>
  )
}
export default SystemMessagesSection
